import { useState } from "react"
import { VStack, Box, Text } from "@chakra-ui/react"
import { useParams } from "react-router-dom"
import { getDefaultDateRange } from "@hydrologiq/shared/utils/reporting/dateHelpers"
import { useCustomerPerformance } from "../hooks/useCustomerPerformance"
import CustomerPerformanceFilters from "../components/performance/CustomerPerformanceFilters"
import CustomerPerformanceSummary from "../components/performance/CustomerPerformanceSummary"
import CustomerPerformanceChart from "../components/performance/CustomerPerformanceChart"

export default function CustomerPerformance() {
  const { customerId } = useParams<{ customerId: string }>()
  const defaultDateRange = getDefaultDateRange()

  const [startDate, setStartDate] = useState(defaultDateRange.start)
  const [endDate, setEndDate] = useState(defaultDateRange.end)
  const [chartView, setChartView] = useState<"daily" | "cumulative">("daily")

  const { dailySummary, summary, loading, error } = useCustomerPerformance({
    customerId,
    startDate,
    endDate,
    includeScheduled: true, // Always include scheduled now
    enabled: !!customerId,
  })

  const handleDateRangeChange = (start: string, end: string) => {
    setStartDate(start)
    setEndDate(end)
  }

  const handleChartViewChange = (view: "daily" | "cumulative") => {
    setChartView(view)
  }

  if (error) {
    return (
      <Box p={8} textAlign="center">
        <VStack gap={2}>
          <Text fontSize="lg" fontWeight="semibold" color="red.500">
            Error loading performance data
          </Text>
          <Text fontSize="sm" color="fg.muted">
            {error.message}
          </Text>
        </VStack>
      </Box>
    )
  }

  return (
    <VStack align="stretch" gap={6} p={6}>
      {/* Filters */}
      <CustomerPerformanceFilters
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
      />

      {/* Summary Cards */}
      {summary && <CustomerPerformanceSummary summary={summary} loading={loading} />}

      {/* Chart */}
      <CustomerPerformanceChart
        dailySummary={dailySummary}
        loading={loading}
        chartView={chartView}
        onChartViewChange={handleChartViewChange}
        startDate={startDate}
        endDate={endDate}
      />
    </VStack>
  )
}

